import React, { Fragment, memo, useCallback, useMemo, useState } from 'react'
import { withStyles, useTheme } from '@material-ui/core/styles'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Divider from '@material-ui/core/Divider'
import IconButton from '@material-ui/core/IconButton'
import SvgIcon from '@material-ui/core/SvgIcon'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import LinearProgress from '@material-ui/core/LinearProgress'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction'
import Typography from '@material-ui/core/Typography'
import { processLargeArrayAsync } from '../../data/UnitOfWork'
import LOG from '../../logging/Logger'
import { FOCUS } from '../../data/focus'
import {
  EXPIRY_INTERVAL_COMPLETE_DAYS,
  EXPIRY_INTERVAL_TRASH_DAYS
} from '../../data/intervals'
import {
  HOUSEKEEPING_COMPLETE_THRESHOLD,
  HOUSEKEEPING_TRASH_THRESHOLD
} from '../../data/limits'

const styles = theme => ({
  iconButton: {
    padding: 0
  },
  iconOuter: {},
  iconInner: { color: theme.palette.warning.main },
  list: {
    margin: theme.spacing(1)
  },
  listItem: {
    minHeight: '75px'
  },
  secondary: {
    right: 0
  }
})

const HOUSEKEEPING_PATH =
  'M19.36,2.72L20.78,4.14L15.06,9.85C16.13,11.39 16.28,13.24 15.38,14.44L9.06,8.12C10.26,7.22 12.11,7.37 13.65,8.44L19.36,2.72M5.93,17.57C3.92,15.56 2.69,13.16 2.35,10.92L7.23,8.83L14.67,16.27L12.58,21.15C10.34,20.81 7.94,19.58 5.93,17.57Z'

export default memo(
  withStyles(styles)(({ classes, getTodos, deleteTodo, moveTodoToTrash }) => {
    LOG.debug('Render: Housekeeping')

    const theme = useTheme()

    const [open, setOpen] = useState(false)

    const [cleaningCompletedItems, setCleaningCompletedItems] = useState(0)
    const [
      cleanCompletedItemsProgress,
      setCleanCompletedItemsProgress
    ] = useState(0)

    const [cleaningTrash, setCleaningTrash] = useState(0)
    const [cleanTrashProgress, setCleanTrashProgress] = useState(0)

    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'), {
      noSsr: true
    })

    const completeItems = useMemo(() => {
      return getTodos(
        null,
        true,
        true,
        null,
        null,
        null,
        null,
        null,
        null
      ).filter(todo => todo.list !== FOCUS.TRASH.id && todo.status === 'done')
    }, [getTodos])

    const trashItems = useMemo(() => {
      return getTodos(
        FOCUS.TRASH.id,
        true,
        true,
        null,
        null,
        null,
        null,
        null,
        null
      )
    }, [getTodos])

    const handleOpen = useCallback(() => {
      setOpen(true)
      setCleaningCompletedItems(0)
      setCleaningTrash(0)
    }, [])
    const handleClose = useCallback(() => setOpen(false), [])

    const doClean = useCallback(
      (items, setProgress, setCleaning, cleanCallback) => {
        if (items.length > 0) {
          setCleaning(1)
          setProgress(0)
          processLargeArrayAsync(
            'Do housekeeping',
            items,
            item => {
              cleanCallback(item)
              setProgress(oldProgress => oldProgress + 100 / items.length)
            },
            () => {
              setCleaning(2)
            }
          )
        }
      },
      []
    )

    const handleCleanCompleted = useCallback(() => {
      doClean(
        completeItems,
        setCleanCompletedItemsProgress,
        setCleaningCompletedItems,
        item => moveTodoToTrash(item, false)
      )
    }, [doClean, completeItems, moveTodoToTrash])

    const handleCleanTrash = useCallback(() => {
      doClean(trashItems, setCleanTrashProgress, setCleaningTrash, item =>
        deleteTodo(item, false)
      )
    }, [doClean, trashItems, deleteTodo])

    const handleCleanAll = useCallback(() => {
      handleCleanCompleted()
      handleCleanTrash()
    }, [handleCleanCompleted, handleCleanTrash])

    return (
      <Fragment>
        {(completeItems.length > HOUSEKEEPING_COMPLETE_THRESHOLD ||
          trashItems.length > HOUSEKEEPING_TRASH_THRESHOLD) && (
          <IconButton className={classes.iconButton} onClick={handleOpen}>
            <div className={classes.iconOuter}>
              <SvgIcon className={classes.iconInner}>
                <path d={HOUSEKEEPING_PATH} />
              </SvgIcon>
            </div>
          </IconButton>
        )}
        <Dialog
          fullScreen={fullScreen}
          open={open}
          onClose={() => handleClose(false)}
        >
          <DialogTitle>Housekeeping</DialogTitle>
          <DialogContent>
            <Typography variant='body1' gutterBottom>
              To keep things running as fast as possible, please consider
              cleaning up old items
            </Typography>
            <List className={classes.list}>
              <Divider />
              <ListItem className={classes.listItem}>
                {cleaningCompletedItems === 0 && completeItems.length > 0 && (
                  <ListItemText
                    primary={`${completeItems.length} completed item${
                      completeItems.length !== 1 ? 's' : ''
                    }`}
                    secondary={`Move item${
                      completeItems.length !== 1 ? 's' : ''
                    } to Trash`}
                  />
                )}
                {cleaningCompletedItems === 0 && completeItems.length === 0 && (
                  <ListItemText primary='No completed items to clean' />
                )}
                {cleaningCompletedItems === 1 && (
                  <ListItemText>
                    <LinearProgress
                      variant='determinate'
                      value={cleanCompletedItemsProgress}
                      style={{ width: '50%' }}
                    />
                  </ListItemText>
                )}
                {cleaningCompletedItems === 2 && (
                  <ListItemText primary='Cleaning complete' />
                )}
                <ListItemSecondaryAction className={classes.secondary}>
                  <Button
                    disabled={
                      cleaningCompletedItems !== 0 || completeItems.length === 0
                    }
                    onClick={handleCleanCompleted}
                    edge='end'
                    aria-label='Move complete items to Trash'
                    color='secondary'
                  >
                    Clean
                  </Button>
                </ListItemSecondaryAction>
              </ListItem>
              <Divider />
              <ListItem className={classes.listItem}>
                {cleaningTrash === 0 && trashItems.length > 0 && (
                  <ListItemText
                    primary={`${trashItems.length} item${
                      trashItems.length !== 1 ? 's' : ''
                    } in trash`}
                    secondary={`Permanently delete item${
                      trashItems.length !== 1 ? 's' : ''
                    }`}
                  />
                )}
                {cleaningTrash === 0 && trashItems.length === 0 && (
                  <ListItemText primary='No items in trash' />
                )}
                {cleaningTrash === 2 && (
                  <ListItemText primary='Cleaning complete' />
                )}
                {cleaningTrash === 1 && (
                  <ListItemText>
                    <LinearProgress
                      variant='determinate'
                      value={cleanTrashProgress}
                      style={{ width: '50%' }}
                    />
                  </ListItemText>
                )}
                <ListItemSecondaryAction className={classes.secondary}>
                  <Button
                    disabled={cleaningTrash !== 0 || trashItems.length === 0}
                    onClick={handleCleanTrash}
                    edge='end'
                    aria-label='Empty Trash'
                    color='secondary'
                  >
                    Clean
                  </Button>
                </ListItemSecondaryAction>
              </ListItem>
              <Divider />
            </List>
            <Typography variant='body2' gutterBottom>
              <b>Note:</b> completed items are automatically moved to Trash
              after {EXPIRY_INTERVAL_COMPLETE_DAYS} days and items in Trash are
              permanently deleted after {EXPIRY_INTERVAL_TRASH_DAYS} days.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCleanAll}
              color='secondary'
              disabled={
                cleaningCompletedItems === 1 ||
                cleaningTrash === 1 ||
                (trashItems.length === 0 && completeItems.length === 0) ||
                (cleaningCompletedItems === 2 && trashItems.length === 0) ||
                (cleaningTrash === 2 && completeItems.length === 0)
              }
            >
              Clean All
            </Button>
            <Button onClick={handleClose} color='primary'>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Fragment>
    )
  })
)
